import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  Collapse,
  Chip,
  useTheme,
  useMediaQuery,
  Button,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Dashboard as DashboardIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { formatCelular } from '../../utils';
import { THEME_COLORS, BREAKPOINTS } from '../../constants';

/**
 * Componente de tabla de distribuidores para vista de escritorio
 */
const DistributorsTable = ({
  distributors,
  expandedDistributorId,
  onExpand,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewDashboard,
  showActions = true,
  loading = false,
  // Props de paginación
  pagination = null,
  onPageChange = null,
  onPageSizeChange = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.MOBILE));

  // Componente de controles de paginación
  const PaginationControls = () => {
    if (!pagination) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 2,
          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
        }}
      >
        {/* Información de registros */}
        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
          Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} distribuidores
        </Typography>

        {/* Controles de navegación */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Selector de elementos por página */}
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={pagination.limit}
              onChange={(e) => onPageSizeChange?.(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                },
                '& .MuiSelect-select': {
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>

          {/* Botones de navegación */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!pagination.hasPrev}
              onClick={() => onPageChange?.(pagination.page - 1)}
              startIcon={<NavigateBeforeIcon />}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                borderColor: THEME_COLORS.BORDER_LIGHT,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: THEME_COLORS.TEXT_PRIMARY,
                  color: THEME_COLORS.TEXT_PRIMARY,
                },
                '&:disabled': {
                  color: THEME_COLORS.TEXT_MUTED,
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                },
              }}
            >
              Anterior
            </Button>

            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, px: 2 }}>
              Página {pagination.page} de {pagination.pages || 1}
            </Typography>

            <Button
              variant="outlined"
              size="small"
              disabled={!pagination.hasNext}
              onClick={() => onPageChange?.(pagination.page + 1)}
              endIcon={<NavigateNextIcon />}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                borderColor: THEME_COLORS.BORDER_LIGHT,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: THEME_COLORS.TEXT_PRIMARY,
                  color: THEME_COLORS.TEXT_PRIMARY,
                },
                '&:disabled': {
                  color: THEME_COLORS.TEXT_MUTED,
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                },
              }}
            >
              Siguiente
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  if (distributors.length === 0) {
    return (
      <Box>
        <TableContainer 
          component={Paper} 
          sx={{ 
            border: `1px solid ${THEME_COLORS.BORDER}`,
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableBody>
              <TableRow>
                <TableCell colSpan={showActions ? 8 : 7} sx={{ textAlign: 'center', padding: 4 }}>
                  <Typography variant="body1" sx={{ color: THEME_COLORS.TEXT_MUTED }}>
                    No se encontraron distribuidores
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <PaginationControls />
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer 
        component={Paper} 
        sx={{ 
          border: `1px solid ${THEME_COLORS.BORDER}`,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: THEME_COLORS.LIGHT_GRAY }}>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Empresa</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Distribuidor</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Contacto</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Cuenta eWeLink</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Ubicación</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Contactos</TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Estado</TableCell>
              {showActions && (
                <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY, textAlign: 'center' }}>
                  Acciones
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {distributors.map((distributor) => (
              <React.Fragment key={distributor.distributor_id}>
                {/* Fila principal */}
                <TableRow
                  sx={{
                    '&:hover': {
                      backgroundColor: THEME_COLORS.LIGHT_GRAY,
                    },
                  }}
                >
                  {/* Empresa */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: THEME_COLORS.TEXT_PRIMARY,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        >
                          {distributor.nombre_empresa}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: THEME_COLORS.TEXT_MUTED,
                            fontSize: '0.75rem',
                          }}
                        >
                          ID: {distributor.distributor_id.slice(0, 8)}...
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => onExpand(distributor.distributor_id)}
                        sx={{ marginLeft: 1, color: THEME_COLORS.SECONDARY }}
                      >
                        <ExpandMoreIcon 
                          sx={{
                            transform: expandedDistributorId === distributor.distributor_id ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease-in-out'
                          }}
                        />
                      </IconButton>
                    </Box>
                  </TableCell>

                  {/* Distribuidor */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY }} />
                      <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY }}>
                        {distributor.nombre_distribuidor}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Contacto */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY }} />
                      <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY }}>
                        {formatCelular(distributor.celular)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Cuenta eWeLink */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY }} />
                      <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY }}>
                        {distributor.cuenta_ewelink}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Ubicación */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY }} />
                      <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY }}>
                        {distributor.ciudad ? `${distributor.ciudad}${distributor.estado ? `, ${distributor.estado}` : ''}` : 'No especificada'}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Contactos */}
                  <TableCell>
                    <Chip
                      label={`${distributor.total_contacts || 0} contactos`}
                      size="small"
                      sx={{
                        backgroundColor: distributor.total_contacts > 0 ? THEME_COLORS.TEXT_PRIMARY : THEME_COLORS.TEXT_MUTED,
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>

                  {/* Estado */}
                  <TableCell>
                    <Chip
                      icon={distributor.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                      label={distributor.is_active ? 'Activo' : 'Inactivo'}
                      size="small"
                      sx={{
                        backgroundColor: distributor.is_active ? THEME_COLORS.TEXT_SECONDARY : THEME_COLORS.TEXT_MUTED,
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        border: 'none',
                        '& .MuiChip-icon': {
                          color: 'white',
                          fontSize: '16px',
                        },
                      }}
                    />
                  </TableCell>

                  {/* Acciones */}
                  {showActions && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title="Ver Dashboard" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onViewDashboard(distributor)}
                            sx={{
                              color: THEME_COLORS.SECONDARY,
                              '&:hover': { color: THEME_COLORS.TEXT_PRIMARY },
                            }}
                          >
                            <DashboardIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onEdit(distributor)}
                            sx={{
                              color: THEME_COLORS.SECONDARY,
                              '&:hover': { color: THEME_COLORS.PRIMARY },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={distributor.is_active ? "Desactivar" : "Activar"} arrow>
                          <IconButton
                            size="small"
                            onClick={() => onToggleStatus(distributor)}
                            sx={{
                              color: THEME_COLORS.SECONDARY,
                              '&:hover': { color: distributor.is_active ? THEME_COLORS.TEXT_MUTED : THEME_COLORS.TEXT_SECONDARY },
                            }}
                          >
                            {distributor.is_active ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onDelete(distributor)}
                            sx={{
                              color: THEME_COLORS.SECONDARY,
                              '&:hover': { color: THEME_COLORS.ERROR },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>

                {/* Fila expandible con detalles */}
                <TableRow>
                  <TableCell colSpan={showActions ? 8 : 7} sx={{ padding: 0, border: 'none' }}>
                    <Collapse in={expandedDistributorId === distributor.distributor_id}>
                      <Box sx={{ 
                        padding: 3, 
                        backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT, 
                        borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                      }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 2, fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                          Detalles de {distributor.nombre_empresa}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                              ID de Distribuidor
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                              {distributor.distributor_id}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                              Nombre del Distribuidor
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                              {distributor.nombre_distribuidor}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                              Celular
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                              {formatCelular(distributor.celular)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                              Cuenta eWeLink
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                              {distributor.cuenta_ewelink}
                            </Typography>
                          </Box>
                          {distributor.numero_cuenta_bancaria && (
                            <Box>
                              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                                Cuenta Bancaria
                              </Typography>
                              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                                {distributor.numero_cuenta_bancaria}
                              </Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                              Estadísticas
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip
                                label={`${distributor.total_contacts || 0} contactos`}
                                size="small"
                                sx={{ backgroundColor: THEME_COLORS.BORDER, color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={`${distributor.active_subscriptions || 0} activas`}
                                size="small"
                                sx={{ backgroundColor: THEME_COLORS.LIGHT_GRAY, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={`$${(distributor.total_revenue || 0).toLocaleString('es-CO')}`}
                                size="small"
                                sx={{ backgroundColor: THEME_COLORS.BACKGROUND_PAPER, color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationControls />
    </Box>
  );
};

export default DistributorsTable;